// Utility to parse markdown tables into array of objects
// Usage: parseMarkdownTable(markdownString)

// Splits a markdown table row into cells, preserving empty cells via positional mapping.
const splitTableRow = (line) => {
  const trimmed = line.trim();
  const inner = (trimmed.startsWith('|') && trimmed.endsWith('|'))
    ? trimmed.slice(1, -1)   // strip leading/trailing pipe
    : trimmed;
  return inner.split('|').map(cell => cell.trim());
};

const parseMarkdownTable = (markdown) => {
  if (!markdown || typeof markdown !== 'string') return [];

  const lines = markdown.trim().split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];

  // Skip any markdown headings (lines starting with #) to find the table
  let tableStartIndex = 0;
  while (tableStartIndex < lines.length && lines[tableStartIndex].trim().startsWith('#')) {
    tableStartIndex++;
  }

  // If we skipped past all lines, no table found
  if (tableStartIndex >= lines.length) return [];

  // Extract lines from table start
  const tableLines = lines.slice(tableStartIndex);
  if (tableLines.length < 2) return [];

  // First line of table is headers — filter empty to remove accidental leading/trailing empties
  const headers = splitTableRow(tableLines[0]).filter(h => h !== '');

  if (headers.length === 0) return [];

  // Find the separator line (contains dashes) and skip it
  let dataStartIndex = 1;
  if (tableLines[1] && tableLines[1].includes('-')) {
    dataStartIndex = 2;
  }

  // Parse data rows — use positional mapping so empty cells keep their column position
  const rows = [];
  for (let i = dataStartIndex; i < tableLines.length; i++) {
    const cells = splitTableRow(tableLines[i]);
    if (cells.length === 0 || cells.every(c => c === '')) continue;
    const row = {};
    headers.forEach((header, index) => {
      row[header] = cells[index] !== undefined ? cells[index] : '';
    });
    rows.push(row);
  }
  return rows;
};

// Extracts the display value from a SQL response that may be in {markdown, records} JSON format.
// Returns parsed.markdown (string) or parsed.records (array) if present, otherwise returns the input unchanged.
const extractSqlTableData = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      // Prefer the pre-formatted markdown table string; fall back to records array
      if (typeof parsed.markdown === "string") return parsed.markdown;
      if (Array.isArray(parsed.records)) return parsed.records;
    }
  } catch (e) {
    // Not JSON, keep as-is
  }
  return value;
};

// Detects when a value is a JSON array of agent execution records
// (objects with 'answer' + 'phase'/'agent_id' fields) and extracts
// the actual answer from the direct_execution phase record (or last record).
// Returns the extracted answer string/value, or null if not applicable.
const extractAgentExecutionAnswer = (value) => {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (
      Array.isArray(parsed) &&
      parsed.length > 0 &&
      parsed[0] != null &&
      typeof parsed[0] === "object" &&
      "answer" in parsed[0] &&
      ("phase" in parsed[0] || "agent_id" in parsed[0])
    ) {
      const record =
        parsed.find((r) => r.phase === "direct_execution") ||
        parsed[parsed.length - 1];
      return record.answer != null ? record.answer : null;
    }
  } catch (e) {
    // Not applicable
  }
  return null;
};

// Combines extractAgentExecutionAnswer with the markdown-table detection pattern
// used in both ChatScreen and MainContent. Returns { answer, isSql } if an agent
// execution answer is found, otherwise returns null.
const processAgentExecutionAnswer = (message) => {
  const agentAnswer = extractAgentExecutionAnswer(message);
  if (agentAnswer !== null) {
    const isSql = typeof agentAnswer === "string" && agentAnswer.trim().startsWith("|");
    return { answer: agentAnswer, isSql };
  }
  return null;
};

// Parses an HTML table string into an array of row objects (same shape as parseMarkdownTable).
// Cell values are stored as innerHTML when they contain HTML tags (preserving links/images),
// otherwise as plain text.
const parseHTMLTable = (htmlString) => {
  if (!htmlString || typeof htmlString !== 'string') return [];
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const table = doc.querySelector('table');
    if (!table) return [];

    const headers = [];
    const headerRow = table.querySelector('thead tr');
    if (headerRow) {
      headerRow.querySelectorAll('th').forEach(th => headers.push(th.textContent.trim()));
    }
    // Fallback: first row th elements
    if (headers.length === 0) {
      const firstRow = table.querySelector('tr');
      if (firstRow) {
        firstRow.querySelectorAll('th').forEach(th => headers.push(th.textContent.trim()));
      }
    }
    if (headers.length === 0) return [];

    const rows = [];
    table.querySelectorAll('tbody tr').forEach(tr => {
      const row = {};
      tr.querySelectorAll('td').forEach((td, i) => {
        if (i < headers.length) {
          const inner = td.innerHTML.trim();
          row[headers[i]] = inner.includes('<') ? inner : td.textContent.trim();
        }
      });
      if (Object.keys(row).length > 0) rows.push(row);
    });
    return rows;
  } catch (e) {
    console.error('Error parsing HTML table:', e);
    return [];
  }
};

module.exports = { parseMarkdownTable, parseHTMLTable, extractSqlTableData, extractAgentExecutionAnswer, processAgentExecutionAnswer };